'use client'

import Link from 'next/link'
import { useState } from 'react'

import {
  GUIDE_FIELD_MODE,
  type FieldModeEvent,
  type FieldModeLink,
} from '@/content/guide-field-mode'

type AnalyticsWindow = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
}

const sendGoogleAnalytics = (
  event: FieldModeEvent | 'official_source_open' | 'report_outdated_open',
  slug: string,
  action: string,
) => {
  try {
    if (localStorage.getItem('wm_cookie_consent') !== 'accepted') return

    const analyticsWindow = window as AnalyticsWindow
    analyticsWindow.dataLayer = analyticsWindow.dataLayer || []
    const gtag =
      analyticsWindow.gtag ||
      ((...args: unknown[]) => {
        analyticsWindow.dataLayer?.push(args)
      })

    gtag('event', event, {
      field_action: action,
      guide_slug: slug,
    })
  } catch {
    // First-party measurement remains available when browser storage is blocked.
  }
}

const sendFieldEvent = (
  event: FieldModeEvent | 'official_source_open' | 'report_outdated_open',
  slug: string,
  action: string,
) => {
  const target = `guide:${slug}/${action}`
  const payload = JSON.stringify({ event, target, tool: 'field-mode' })

  try {
    const blob = new Blob([payload], { type: 'application/json' })
    if (navigator.sendBeacon('/api/tool-events', blob)) {
      sendGoogleAnalytics(event, slug, action)
      return
    }
  } catch {
    // Fall through to keepalive fetch.
  }

  void fetch('/api/tool-events', {
    method: 'POST',
    body: payload,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
  }).catch(() => undefined)

  sendGoogleAnalytics(event, slug, action)
}

const trackedClick = (slug: string, link: FieldModeLink, className: string) => {
  const onClick = () => sendFieldEvent(link.event, slug, link.action)

  if (link.external) {
    return (
      <a
        className={className}
        href={link.href}
        onClick={onClick}
        rel="noopener noreferrer"
        target="_blank"
      >
        {link.label} <span aria-hidden="true">↗</span>
      </a>
    )
  }

  if (link.href.startsWith('#')) {
    return (
      <a className={className} href={link.href} onClick={onClick}>
        {link.label} <span aria-hidden="true">↓</span>
      </a>
    )
  }

  return (
    <Link className={className} href={link.href} onClick={onClick}>
      {link.label} <span aria-hidden="true">→</span>
    </Link>
  )
}

const formatVerifiedDate = (value: string) =>
  new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00.000Z`))

export const hasGuideFieldMode = (slug: string) => Boolean(GUIDE_FIELD_MODE[slug])

export const GuideFieldMode = ({ slug }: { slug: string }) => {
  const config = GUIDE_FIELD_MODE[slug]
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  if (!config) return null

  const copyChecklist = async () => {
    const value = config.checklist.map((item, index) => `${index + 1}. ${item}`).join('\n')
    try {
      await navigator.clipboard.writeText(value)
      setCopyState('copied')
    } catch {
      try {
        const textarea = document.createElement('textarea')
        textarea.value = value
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        const copied = document.execCommand('copy')
        textarea.remove()
        setCopyState(copied ? 'copied' : 'failed')
      } catch {
        setCopyState('failed')
      }
    }
    sendFieldEvent('field_action_click', slug, 'copy-checklist')
  }

  return (
    <section className="guide-field-mode" id="field-mode" aria-labelledby="guide-field-mode-title">
      <div className="guide-field-mode__eyebrow">
        <span className="guide-section-v2__badge">FIELD MODE</span>
        <span>Verified {formatVerifiedDate(config.verifiedOn)}</span>
      </div>

      <div className="guide-field-mode__body">
        <div className="guide-field-mode__answer">
          <h2 id="guide-field-mode-title">What to do right now</h2>
          <p>{config.fastAnswer}</p>
          <div className="guide-field-mode__actions">
            {trackedClick(slug, config.primaryAction, 'guide-field-mode__primary')}
            {config.backupLink
              ? trackedClick(slug, config.backupLink, 'guide-field-mode__secondary')
              : null}
          </div>
        </div>

        <aside className="guide-field-mode__backup" aria-label="Backup action">
          <span>Backup</span>
          <p>{config.backup}</p>
        </aside>
      </div>

      <details className="guide-field-mode__checklist">
        <summary>Quick checklist</summary>
        <ol>
          {config.checklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
        <button type="button" onClick={copyChecklist}>
          {copyState === 'copied'
            ? 'Checklist copied'
            : copyState === 'failed'
              ? 'Copy failed — select manually'
              : 'Copy checklist'}
        </button>
      </details>

      <div className="guide-field-mode__footer">
        <div className="guide-field-mode__sources">
          <span>Current source</span>
          {config.sources.map((source, index) => (
            <a
              href={source.href}
              key={source.href}
              onClick={() => sendFieldEvent('official_source_open', slug, `source-${index + 1}`)}
              rel="noopener noreferrer"
              target="_blank"
            >
              {source.label} ↗
            </a>
          ))}
        </div>
        <Link
          className="guide-field-mode__report"
          href="/contact"
          onClick={() => sendFieldEvent('report_outdated_open', slug, 'report-outdated')}
        >
          Report outdated info
        </Link>
      </div>
    </section>
  )
}
